import { useMemo } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { CALENDAR_Z_INDICES } from '../../constants';
import { useCalendarGridStore } from '../../store/calendar-grid.store';
import { minutesToTimeStr } from '../../utils';
import { TooltipContainer } from '../calendar-tooltip/tooltip-container';

type DragCreateSelectionOverlayProps = {
  isEnabled?: boolean;
};

export const DragCreateSelectionOverlay = ({ isEnabled }: DragCreateSelectionOverlayProps) => {
  const { dragCreateSelection, groupIds, groupsById } = useCalendarGridStore([
    'dragCreateSelection',
    'groupIds',
    'groupsById',
  ]);

  const groupId = dragCreateSelection?.groupId;
  const columnId = dragCreateSelection?.columnId;

  const dragCreateOverlayColumn = useMemo(() => {
    if (!isEnabled || !groupId || !columnId) return undefined;

    const groupIndex = groupIds.indexOf(groupId);
    const groupInfo = groupsById[groupId];
    if (groupIndex < 0 || !groupInfo) return undefined;

    const columnIndex = groupInfo.columnIds.indexOf(columnId);
    if (columnIndex < 0) return undefined;

    // +1 time column, +1 CSS grid 1-based indexing
    const startColumn = groupIndex + groupInfo.groupOffset + 2 + columnIndex;

    return `${startColumn} / span 1`;
  }, [columnId, groupId, groupIds, groupsById, isEnabled]);

  if (!isEnabled || !dragCreateSelection || !dragCreateOverlayColumn) {
    return null;
  }

  const startTime = minutesToTimeStr(dragCreateSelection.startMinutes);
  const endTime = minutesToTimeStr(dragCreateSelection.endMinutes);

  const minMinutes = Math.min(dragCreateSelection.startMinutes, dragCreateSelection.endMinutes);
  const maxMinutes = Math.max(dragCreateSelection.startMinutes, dragCreateSelection.endMinutes);
  // +1 because row 1 represents minute 0; ensure at least a 1-row span when duration is 0
  const rowStart = minMinutes + 1;
  const rowEnd = Math.max(maxMinutes + 1, rowStart + 1);

  return (
    <div aria-hidden='true' css={dragCreateOverlayColumnStyle} style={{ gridColumn: dragCreateOverlayColumn }}>
      <div css={dragCreateSelectionStyle} style={{ gridRow: `${rowStart} / ${rowEnd}` }}>
        <TooltipContainer css={tooltipContainerStyle}>
          {dragCreateSelection.startMinutes <= dragCreateSelection.endMinutes
            ? `${startTime} - ${endTime}`
            : `${endTime} - ${startTime}`}
        </TooltipContainer>
      </div>
    </div>
  );
};

const dragCreateOverlayColumnStyle = css({
  gridRow: '4 / -2',
  display: 'grid',
  gridTemplateRows: 'repeat(var(--total-mins), var(--row-height))',
  position: 'relative',
  pointerEvents: 'none',
  zIndex: CALENDAR_Z_INDICES.dragCreateSelectionOverlay,
});

const dragCreateSelectionStyle = css({
  position: 'relative',
  gridColumn: 1,
  borderRadius: theme.borderRadius.small,
  backgroundColor: theme.colors.primary20,
  border: `1.5px dashed ${theme.colors.primary40}`,
  boxShadow: theme.shadows.light,
  pointerEvents: 'none',
  boxSizing: 'border-box',
});

const tooltipContainerStyle = css({
  position: 'absolute',
  top: theme.spacing(-5),
  right: '50%',
  transform: 'translateX(50%) !important',
});
